import type { LandingTestimonialAdmin } from '@repo/api-contracts';
import {
  type ButtonHTMLAttributes,
  type ChangeEvent,
  type HTMLAttributes,
  type ReactElement,
  useRef,
} from 'react';
import { cn, DropdownMenu, OrderMoveControls } from '@/shared/ui';

interface LandingTestimonialCardProps {
  readonly item: LandingTestimonialAdmin;
  /** 1부터 시작하는 노출 순번. */
  readonly order: number;
  readonly isFirst: boolean;
  readonly isLast: boolean;
  readonly isBusy: boolean;
  readonly onMove: (direction: 'UP' | 'DOWN') => void;
  readonly onEdit: () => void;
  readonly onReplace: (file: File) => void;
  readonly onDelete: () => void;
  /** 끌기 손잡이(⋮⋮)에 얹는 드래그 속성 — useReorderDrag.handleProps. */
  readonly handleProps?: ButtonHTMLAttributes<HTMLButtonElement>;
  /** 카드(li)에 얹는 놓기 대상 속성 — useReorderDrag.rowProps. */
  readonly rowProps?: HTMLAttributes<HTMLLIElement>;
  /** 끌어 오는 중 이 카드의 어느 쪽에 놓일지 표시. */
  readonly dropEdge?: 'before' | 'after';
}

const ACCEPT = 'image/jpeg,image/png,image/webp';

/**
 * 기업 고객 사례 한 장 — 사진, 순번, 제목·본문·담당자. 아래 줄에 끌기 손잡이와 앞·뒤 화살표,
 * 수정·사진 교체·삭제는 미트볼(⋯) 메뉴 안에 있다. 사진 교체의 파일 input 은 숨기고 메뉴에서 연다.
 */
export const LandingTestimonialCard = ({
  item,
  order,
  isFirst,
  isLast,
  isBusy,
  onMove,
  onEdit,
  onReplace,
  onDelete,
  handleProps,
  rowProps,
  dropEdge,
}: LandingTestimonialCardProps): ReactElement => {
  const title = `${order}번 기업 고객 사례`;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleReplace = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];

    if (file !== undefined) {
      onReplace(file);
    }
    // 같은 파일을 다시 골라도 change 가 나도록 비운다.
    event.target.value = '';
  };

  return (
    <li {...rowProps} className="relative flex flex-col gap-3 rounded-lg border p-3">
      {dropEdge ? (
        <span
          className={cn(
            'pointer-events-none absolute inset-y-0 z-10 border-l-2 border-primary',
            dropEdge === 'before' ? '-left-2' : '-right-2',
          )}
          aria-hidden="true"
        />
      ) : null}
      <div className="relative aspect-[4/3] overflow-hidden rounded-md bg-muted">
        <img
          src={item.imageUrl}
          alt={item.title}
          draggable={false}
          className="size-full object-cover"
        />
        <span className="absolute top-2 left-2 rounded-full bg-background px-2.5 py-0.5 text-xs font-semibold tabular-nums">
          {order}
        </span>
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{item.title}</p>
        <p className="mt-1 line-clamp-3 text-sm text-muted-foreground whitespace-pre-line">
          {item.body}
        </p>
        <p className="mt-2 truncate text-sm font-medium">{item.author}</p>
      </div>

      <div className="flex items-center justify-between border-t pt-2">
        <OrderMoveControls
          orientation="horizontal"
          title={title}
          first={isFirst}
          last={isLast}
          disabled={isBusy}
          onMove={onMove}
          handleProps={handleProps}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          disabled={isBusy}
          onChange={handleReplace}
          aria-label={`${title} 사진 교체`}
          className="sr-only"
        />
        {/* 요청 중에는 메뉴를 감춘다 — 잠긴 화면에서 수정·삭제가 겹쳐 들어가지 않게. */}
        {isBusy ? null : (
          <DropdownMenu
            ariaLabel={`${title} 메뉴`}
            items={[
              { label: '수정', onSelect: onEdit },
              { label: '사진 교체', onSelect: () => fileInputRef.current?.click() },
              { label: '삭제', isDestructive: true, onSelect: onDelete },
            ]}
          />
        )}
      </div>
    </li>
  );
};
